/**
 * Gestión → Administración → Conectividad Hermes
 * (`/gestion/administracion/hermes`).
 *
 * Puerto nativo de `HermesConnectivity::index` + `views/menu/hermes_connectivity/index.php`
 * y de `src/hermes/connectivity.js`: consulta las transacciones de los equipos
 * Hermes por fecha, prueba la conectividad, gestiona el cierre diario (crear,
 * actualizar y deshacer) y exporta transacciones y cierres a Excel.
 */

import {
  CalendarDays,
  CloudDownload,
  Download,
  Landmark,
  Link2,
  RefreshCw,
  RotateCcw,
  Save,
  Search,
  Wallet,
} from 'lucide-react';
import { useState } from 'react';

import { useQuery } from '@tanstack/react-query';

import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { ApiError } from '@/lib/http/api-error';

import { AdministracionNav } from '../components/administracion-nav';
import { fechaCorta, fechaHora, monto } from '../gestion-formato';
import { hermesApi, hermesKeys, useAccionCierre, useCierres } from '../hermes.api';

/** Fecha de hoy en el formato del campo `date` (`AAAA-MM-DD`). */
function hoyIso(): string {
  return new Date().toISOString().slice(0, 10);
}

export function HermesPage() {
  useDocumentTitle('Gestión · Conectividad Hermes');

  const [fecha, setFecha] = useState(hoyIso);
  const [consultado, setConsultado] = useState(false);
  const [observaciones, setObservaciones] = useState('');
  const [mensaje, setMensaje] = useState<{ tipo: 'exito' | 'error'; texto: string } | null>(null);
  const [exportando, setExportando] = useState(false);

  const configuracion = useQuery({
    queryKey: hermesKeys.configuracion(),
    queryFn: ({ signal }) => hermesApi.configuracion(signal),
  });

  const consulta = useQuery({
    queryKey: hermesKeys.transacciones(fecha),
    queryFn: ({ signal }) => hermesApi.transacciones(fecha, signal),
    enabled: consultado,
  });

  const cierres = useCierres();
  const { crear, actualizar, deshacer } = useAccionCierre();

  const datos = consulta.data;

  const ejecutar = async (promesa: Promise<unknown>, exito: string) => {
    setMensaje(null);

    try {
      await promesa;
      setMensaje({ tipo: 'exito', texto: exito });
    } catch (error) {
      setMensaje({
        tipo: 'error',
        texto: error instanceof ApiError ? error.message : 'No se pudo completar la acción.',
      });
    }
  };

  const exportar = async (promesa: Promise<void>) => {
    setMensaje(null);
    setExportando(true);

    try {
      await promesa;
    } catch (error) {
      setMensaje({
        tipo: 'error',
        texto: error instanceof Error ? error.message : 'No se pudo exportar.',
      });
    } finally {
      setExportando(false);
    }
  };

  const config = configuracion.data;
  const configCompleta = config
    ? config.url_configurada &&
      config.usuario_configurado &&
      config.password_configurado &&
      config.equipo_configurado &&
      config.apikey_configurada
    : false;

  return (
    <div className="space-y-5">
      <header>
        <h1 className="text-2xl font-bold text-ink-900">Conectividad Hermes</h1>
        <p className="mt-1 text-sm text-ink-500">
          Transacciones de depósitos y retiros de los equipos Hermes, con cierre diario local.
        </p>
      </header>

      <AdministracionNav active="hermes" />

      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-3">
            <Input
              label="Fecha"
              type="date"
              value={fecha}
              onChange={(evento) => {
                setFecha(evento.target.value);
                setConsultado(false);
              }}
              leftIcon={<CalendarDays className="size-4" aria-hidden="true" />}
            />

            <Button
              size="sm"
              loading={consulta.isFetching}
              leftIcon={<Search className="size-4" aria-hidden="true" />}
              onClick={() => {
                setMensaje(null);
                setConsultado(true);
              }}
            >
              Consultar
            </Button>

            <Button
              variant="outline"
              size="sm"
              leftIcon={<Link2 className="size-4" aria-hidden="true" />}
              onClick={() =>
                void ejecutar(hermesApi.conectividad(), 'Conectividad comprobada con la API Hermes.')
              }
            >
              Probar conectividad
            </Button>

            <div className="ml-auto flex items-center gap-2">
              <Badge variant={configCompleta ? 'success' : 'warning'}>
                {configCompleta ? 'Configuración completa' : 'Configuración incompleta'}
              </Badge>
            </div>
          </div>

          {configuracion.isLoading ? <Skeleton className="mt-3 h-4 w-64" /> : null}

          {config ? (
            <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-ink-500">
              <span>URL: {config.url_configurada ? '✓' : '✗'}</span>
              <span>Usuario: {config.usuario_configurado ? '✓' : '✗'}</span>
              <span>Contraseña: {config.password_configurado ? '✓' : '✗'}</span>
              <span>Equipo: {config.equipo_configurado ? '✓' : '✗'}</span>
              <span>API Key: {config.apikey_configurada ? '✓' : '✗'}</span>
            </div>
          ) : null}
        </CardContent>
      </Card>

      {mensaje ? (
        <Alert
          variant={mensaje.tipo === 'exito' ? 'success' : 'error'}
          title={mensaje.tipo === 'exito' ? 'Listo' : 'Error'}
        >
          {mensaje.texto}
        </Alert>
      ) : null}

      {consulta.isError ? (
        <Alert variant="error" title="No se pudieron leer las transacciones">
          {consulta.error instanceof ApiError ? consulta.error.message : 'Inténtalo de nuevo.'}
        </Alert>
      ) : null}

      {!consultado ? (
        <div className="rounded-card border border-dashed border-ink-200 bg-white/70 px-6 py-14 text-center">
          <span className="mx-auto flex size-12 items-center justify-center rounded-2xl bg-ink-100 text-ink-400">
            <Landmark className="size-6" aria-hidden="true" />
          </span>
          <h2 className="mt-4 text-base font-semibold text-ink-800">
            Consulta las transacciones de una fecha
          </h2>
          <p className="mx-auto mt-1.5 max-w-md text-sm leading-relaxed text-ink-500">
            Elige la fecha y pulsa Consultar. Si el día ya está cerrado, los datos salen del
            cierre local; si no, se piden a la API de Hermes.
          </p>
        </div>
      ) : null}

      {consulta.isLoading ? <Skeleton className="h-48 w-full" /> : null}

      {consultado && datos ? (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              icono={CalendarDays}
              etiqueta="Transacciones"
              valor={String(datos.total_transacciones)}
              detalle={datos.cerrado ? `Cierre local · ${fechaHora(datos.fecha_cierre)}` : 'Desde API Hermes'}
            />
            <StatCard
              icono={Landmark}
              etiqueta="Depósitos"
              valor={String(datos.transacciones.filter((t) => t.tipo_dinero === '1').length)}
            />
            <StatCard
              icono={Wallet}
              etiqueta="Retiros"
              valor={String(datos.transacciones.filter((t) => t.tipo_dinero !== '1').length)}
            />
            <StatCard
              icono={Wallet}
              etiqueta="Monto total"
              valor={`S/ ${monto(datos.transacciones.reduce((suma, t) => suma + (t.monto_deposito ?? 0), 0))}`}
            />
          </div>

          <Card>
            <CardContent className="p-4">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                <h2 className="text-sm font-semibold text-ink-800">
                  Transacciones del {fechaCorta(fecha)}
                </h2>

                <Button
                  variant="outline"
                  size="sm"
                  loading={exportando}
                  leftIcon={<Download className="size-4" aria-hidden="true" />}
                  onClick={() => void exportar(hermesApi.exportarTransacciones(fecha))}
                >
                  Exportar
                </Button>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full min-w-[760px] text-left text-sm">
                  <thead className="border-b border-ink-100 text-xs uppercase tracking-wide text-ink-400">
                    <tr>
                      <th className="py-2 pr-3 font-semibold">Equipo</th>
                      <th className="py-2 pr-3 font-semibold">Tipo</th>
                      <th className="py-2 pr-3 font-semibold">Depósito</th>
                      <th className="py-2 pr-3 font-semibold">Usuario</th>
                      <th className="py-2 pr-3 font-semibold text-right">Monto</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-ink-50">
                    {datos.transacciones.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="py-8 text-center text-sm text-ink-400">
                          No hay transacciones para esta fecha.
                        </td>
                      </tr>
                    ) : (
                      datos.transacciones.map((transaccion, indice) => (
                        <tr key={`${transaccion.numero_transaccion}-${indice}`}>
                          <td className="py-2 pr-3 font-mono text-xs">{transaccion.codigo_equipo}</td>
                          <td className="py-2 pr-3">
                            <Badge variant={transaccion.tipo_dinero === '1' ? 'success' : 'neutral'}>
                              {transaccion.tipo_dinero === '1' ? 'Depósito' : 'Retiro'}
                            </Badge>
                          </td>
                          <td className="py-2 pr-3 font-mono text-xs">{transaccion.codigo_deposito}</td>
                          <td className="py-2 pr-3 text-ink-600">{transaccion.nombre_usuario}</td>
                          <td className="py-2 pr-3 text-right font-semibold tabular-nums">
                            S/ {monto(transaccion.monto_deposito)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h2 className="text-sm font-semibold text-ink-800">Cierre del día</h2>
                  <p className="text-xs text-ink-500">
                    {datos.cerrado
                      ? 'El día está cerrado: las consultas salen de la copia local.'
                      : 'El día aún no está cerrado: las consultas salen de la API.'}
                  </p>
                </div>

                <Badge variant={datos.cerrado ? 'success' : 'neutral'}>
                  {datos.cerrado ? 'Cerrado' : 'Abierto'}
                </Badge>
              </div>

              <div className="flex flex-wrap items-end gap-3">
                <Input
                  label="Observaciones"
                  placeholder="Opcional"
                  value={observaciones}
                  onChange={(evento) => setObservaciones(evento.target.value)}
                  className="min-w-56 flex-1"
                />

                {!datos.cerrado ? (
                  <Button
                    size="sm"
                    loading={crear.isPending}
                    leftIcon={<Save className="size-4" aria-hidden="true" />}
                    onClick={() => void ejecutar(crear.mutateAsync({ fecha, observaciones }), 'Cierre creado exitosamente.')}
                  >
                    Crear cierre
                  </Button>
                ) : (
                  <>
                    <Button
                      size="sm"
                      loading={actualizar.isPending}
                      leftIcon={<RefreshCw className="size-4" aria-hidden="true" />}
                      onClick={() => void ejecutar(actualizar.mutateAsync({ fecha, observaciones }), 'Cierre actualizado exitosamente.')}
                    >
                      Actualizar cierre
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      loading={deshacer.isPending}
                      leftIcon={<RotateCcw className="size-4" aria-hidden="true" />}
                      onClick={() => void ejecutar(deshacer.mutateAsync(fecha), 'Cierre deshecho exitosamente.')}
                    >
                      Deshacer cierre
                    </Button>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        </>
      ) : null}

      <Card>
        <CardContent className="p-4">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-sm font-semibold text-ink-800">Historial de cierres</h2>

            <Button
              variant="outline"
              size="sm"
              loading={exportando}
              leftIcon={<CloudDownload className="size-4" aria-hidden="true" />}
              onClick={() => void exportar(hermesApi.exportarCierres())}
            >
              Exportar
            </Button>
          </div>

          {cierres.isLoading ? (
            <Skeleton className="h-24 w-full" />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px] text-left text-sm">
                <thead className="border-b border-ink-100 text-xs uppercase tracking-wide text-ink-400">
                  <tr>
                    <th className="py-2 pr-3 font-semibold">Fecha</th>
                    <th className="py-2 pr-3 font-semibold text-right">Transacciones</th>
                    <th className="py-2 pr-3 font-semibold text-right">Depósitos</th>
                    <th className="py-2 pr-3 font-semibold text-right">Retiros</th>
                    <th className="py-2 pr-3 font-semibold text-right">Monto general</th>
                    <th className="py-2 pr-3 font-semibold">Creado por</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-ink-50">
                  {(cierres.data?.cierres ?? []).length === 0 ? (
                    <tr>
                      <td colSpan={6} className="py-8 text-center text-sm text-ink-400">
                        Todavía no hay cierres diarios.
                      </td>
                    </tr>
                  ) : (
                    (cierres.data?.cierres ?? []).map((cierre) => (
                      <tr key={cierre.id}>
                        <td className="py-2 pr-3 font-medium text-ink-700">
                          {fechaCorta(cierre.fecha_cierre)}
                        </td>
                        <td className="py-2 pr-3 text-right tabular-nums">{cierre.total_transacciones}</td>
                        <td className="py-2 pr-3 text-right tabular-nums text-emerald-600">
                          S/ {monto(cierre.monto_total_depositos)}
                        </td>
                        <td className="py-2 pr-3 text-right tabular-nums text-rose-600">
                          S/ {monto(cierre.monto_total_retiros)}
                        </td>
                        <td className="py-2 pr-3 text-right font-semibold tabular-nums">
                          S/ {monto(cierre.monto_total_general)}
                        </td>
                        <td className="py-2 pr-3 text-ink-500">{cierre.usuario_nombre ?? 'Sistema'}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

interface StatCardProps {
  icono: typeof CalendarDays;
  etiqueta: string;
  valor: string;
  detalle?: string;
}

function StatCard({ icono: Icono, etiqueta, valor, detalle }: StatCardProps) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <span className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-brand-50 text-brand-600">
            <Icono className="size-4" aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <p className="truncate text-xs font-medium text-ink-500">{etiqueta}</p>
            <p className="truncate text-base font-bold text-ink-900">{valor}</p>
            {detalle ? <p className="truncate text-[11px] text-ink-400">{detalle}</p> : null}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
